import React, { useState, useEffect } from 'react';
import { Calendar, Clock, FileIcon, Play, Mic, Video, Trash2, FileType2, Factory, Pencil, X, Check, CalendarDays, Download } from 'lucide-react';
import { MediaFile, FileType } from '../../types/media';
import MediaPlayer from './MediaPlayer';
import { deleteFile, updateFileName, updateFileDate, updateFileDescription } from '../../services/storage';
import { useAuth } from '../../contexts/AuthContext';
import { factoryList } from '../../data/factories';

interface MediaListProps {
  files: MediaFile[];
  isLoading: boolean;
  onDelete: () => void;
}

const fileTypes: { id: FileType; name: string }[] = [
  { id: 'shutdown', name: 'Shutdown' },
  { id: 'monthly_meeting', name: 'Réunion mensuelle' },
  { id: 'health_safety', name: 'Santé sécurité' },
  { id: 'vs', name: 'VS' },
  { id: 'general_info', name: 'Information générale' }
];

const MediaList: React.FC<MediaListProps> = ({ files: initialFiles, isLoading, onDelete }) => {
  const { currentUser } = useAuth();
  const [files, setFiles] = useState<MediaFile[]>(initialFiles);
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterFileType, setFilterFileType] = useState<FileType | 'all'>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<MediaFile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingFile, setEditingFile] = useState<string | null>(null);
  const [editedName, setEditedName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [editedDate, setEditedDate] = useState('');
  const [editingDescription, setEditingDescription] = useState<string | null>(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState<string | null>(null);

  useEffect(() => {
    setFiles(initialFiles);
  }, [initialFiles]);

  const handleDownload = async (file: MediaFile) => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Une erreur est survenue lors du téléchargement du fichier.');
    }
  };

  const filteredFiles = files.filter(file => {
    const searchTerm = searchQuery.toLowerCase();
    const matchesSearch = 
      file.name.toLowerCase().includes(searchTerm) ||
      (file.description?.toLowerCase().includes(searchTerm) ?? false);
    const matchesFileType = filterFileType === 'all' || file.fileType === filterFileType;
    return matchesSearch && matchesFileType;
  });

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFactoryName = (factoryId: string) => {
    return factoryList.find(f => f.id === factoryId)?.name || factoryId;
  };

  const handleDelete = async (file: MediaFile) => {
    try {
      setIsDeleting(true);
      await deleteFile(file);
      setShowDeleteConfirm(null);
      onDelete();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Une erreur est survenue lors de la suppression du fichier.');
    } finally {
      setIsDeleting(false);
    }
  };

  const startEditing = (file: MediaFile) => {
    setEditingFile(file.id);
    setEditedName(file.name);
    setNameError(null);
    setIsEditing(false);
  };

  const startEditingDate = (file: MediaFile) => {
    setEditingDate(file.id);
    const date = new Date(file.createdAt);
    const formattedDate = date.toISOString().split('T')[0];
    setEditedDate(formattedDate);
    setIsEditing(false);
  };

  const startEditingDescription = (file: MediaFile) => {
    setEditingDescription(file.id);
    setEditedDescription(file.description || '');
    setDescriptionError(null);
    setIsEditing(false);
  };

  const cancelEditing = () => {
    setEditingFile(null);
    setEditingDate(null);
    setEditingDescription(null);
    setEditedName('');
    setEditedDate('');
    setEditedDescription('');
    setNameError(null);
    setDescriptionError(null);
    setIsEditing(false);
  };

  const saveFileName = async (file: MediaFile, e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!editedName.trim()) {
      setNameError('Le nom du fichier ne peut pas être vide.');
      return;
    }

    if (editedName.trim() === file.name) {
      cancelEditing();
      return;
    }

    try {
      setIsEditing(true);
      setNameError(null);
      await updateFileName(file.id, editedName);
      
      const updatedFile = { ...file, name: editedName.trim() };
      setFiles(prevFiles => prevFiles.map(f => f.id === file.id ? updatedFile : f));
      onDelete();
      
      cancelEditing();
    } catch (error) {
      console.error('Error updating file name:', error);
      setNameError(error instanceof Error ? error.message : 'Une erreur est survenue lors de la mise à jour du nom.');
    } finally {
      setIsEditing(false);
    }
  };

  const saveFileDate = async (file: MediaFile, e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!editedDate) {
      cancelEditing();
      return;
    }
    
    try {
      setIsEditing(true);
      const newDate = new Date(editedDate);
      await updateFileDate(file.id, newDate);
      
      const updatedFile = { ...file, createdAt: newDate.getTime() };
      setFiles(prevFiles => prevFiles.map(f => f.id === file.id ? updatedFile : f));
      onDelete();
      
      cancelEditing();
    } catch (error) {
      console.error('Error updating file date:', error);
      alert('Une erreur est survenue lors de la mise à jour de la date.');
    } finally {
      setIsEditing(false);
    }
  };

  const saveFileDescription = async (file: MediaFile, e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    try {
      setIsEditing(true);
      setDescriptionError(null);
      
      if (editedDescription.length > 1000) {
        throw new Error('La description ne peut pas dépasser 1000 caractères.');
      }
      
      await updateFileDescription(file.id, editedDescription);
      
      const updatedFile = { ...file, description: editedDescription.trim() || null };
      setFiles(prevFiles => prevFiles.map(f => f.id === file.id ? updatedFile : f));
      onDelete();
      
      cancelEditing();
    } catch (error) {
      console.error('Error updating file description:', error);
      if (error instanceof Error) {
        setDescriptionError(error.message);
      } else {
        setDescriptionError('Une erreur est survenue lors de la mise à jour de la description.');
      }
    } finally {
      setIsEditing(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, file: MediaFile) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (editingFile) {
        saveFileName(file);
      } else if (editingDate) {
        saveFileDate(file);
      } else if (editingDescription) {
        saveFileDescription(file);
      }
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  if (isLoading) {
    return (
      <div className="my-6 sm:my-8 flex flex-col items-center">
        <div className="animate-pulse space-y-4 w-full">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full mr-3"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!files.length) {
    return (
      <div className="my-6 sm:my-8 text-center py-8 sm:py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <FileIcon className="w-10 sm:w-12 h-10 sm:h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
          Aucun fichier disponible
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Utilisez la zone de téléchargement ci-dessus pour ajouter des fichiers
        </p>
      </div>
    );
  }

  return (
    <div className="my-6 sm:my-8">
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Rechercher des fichiers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        <select
          value={filterFileType}
          onChange={(e) => setFilterFileType(e.target.value as FileType | 'all')}
          className="px-3 sm:px-4 py-2 rounded-lg text-sm font-medium bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">Tous les types</option>
          {fileTypes.map(type => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {filteredFiles.length === 0 ? (
          <div className="text-center py-6 sm:py-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            <p className="text-gray-500 dark:text-gray-400">Aucun résultat trouvé</p>
          </div>
        ) : (
          filteredFiles.map((file) => (
            <div
              key={file.id}
              className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 shadow-sm hover:shadow transition-shadow"
            >
              <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                {file.type === 'video/mp4' ? (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary bg-opacity-10 dark:bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                ) : (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary bg-opacity-10 dark:bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mic className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  {editingFile === file.id ? (
                    <form onSubmit={(e) => saveFileName(file, e)} className="flex items-center gap-2 mb-2">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, file)}
                          className={`w-full px-2 py-1 text-sm sm:text-base border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                            nameError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          disabled={isEditing}
                          autoFocus
                        />
                        {nameError && (
                          <p className="mt-1 text-xs text-red-500">{nameError}</p>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={isEditing || !editedName.trim()}
                        className="p-1 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50 flex-shrink-0"
                      >
                        <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditing}
                        disabled={isEditing}
                        className="p-1 rounded-lg text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex-shrink-0"
                      >
                        <X className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    </form>
                  ) : (
                    <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white truncate mb-2">
                      {file.name}
                    </h3>
                  )}

                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {editingDate === file.id ? (
                      <form onSubmit={(e) => saveFileDate(file, e)} className="flex items-center gap-2">
                        <input
                          type="date"
                          value={editedDate}
                          onChange={(e) => setEditedDate(e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, file)}
                          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                          disabled={isEditing}
                          autoFocus
                        />
                        <button
                          type="submit"
                          disabled={isEditing || !editedDate}
                          className="p-1 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
                        >
                          <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditing}
                          disabled={isEditing}
                          className="p-1 rounded-lg text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                        >
                          <X className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </form>
                    ) : (
                      <>
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(file.createdAt)}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatTime(file.createdAt)}
                        </div>
                      </>
                    )}
                    <div className="flex items-center">
                      <FileType2 className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">{fileTypes.find(t => t.id === file.fileType)?.name}</span>
                      <span className="sm:hidden">{fileTypes.find(t => t.id === file.fileType)?.name.substring(0, 10)}...</span>
                    </div>
                    <div className="flex items-center text-primary">
                      <Factory className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">{getFactoryName(file.factoryId)}</span>
                      <span className="sm:hidden">{getFactoryName(file.factoryId).substring(0, 8)}...</span>
                    </div>
                  </div>

                  <div className="mt-2">
                    {editingDescription === file.id ? (
                      <form onSubmit={(e) => saveFileDescription(file, e)} className="flex items-start gap-2">
                        <div className="flex-1">
                          <textarea
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                saveFileDescription(file);
                              }
                            }}
                            className={`w-full px-2 py-1 text-sm border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                              descriptionError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                            }`}
                            disabled={isEditing}
                            rows={2}
                            placeholder="Ajouter une description..."
                            autoFocus
                          />
                          {descriptionError && (
                            <p className="mt-1 text-xs text-red-500">{descriptionError}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            type="submit"
                            disabled={isEditing}
                            className="p-1 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditing}
                            disabled={isEditing}
                            className="p-1 rounded-lg text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div 
                        className={`text-sm ${
                          file.description 
                            ? 'text-gray-600 dark:text-gray-300' 
                            : 'text-gray-400 dark:text-gray-500 italic'
                        } cursor-pointer hover:text-gray-900 dark:hover:text-gray-200`}
                        onClick={() => currentUser?.id === file.userId && startEditingDescription(file)}
                      >
                        {file.description || (currentUser?.id === file.userId ? "Cliquez pour ajouter une description..." : "")}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 flex-shrink-0">
                  <button 
                    className="p-2 rounded-lg bg-primary bg-opacity-10 dark:bg-opacity-20 hover:bg-opacity-20 dark:hover:bg-opacity-30 transition-colors"
                    onClick={() => setSelectedFile(file)}
                    aria-label="Lire le fichier"
                  >
                    <Play className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  </button>

                  <button 
                    className="p-2 rounded-lg bg-primary bg-opacity-10 dark:bg-opacity-20 hover:bg-opacity-20 dark:hover:bg-opacity-30 transition-colors"
                    onClick={() => handleDownload(file)}
                    aria-label="Télécharger le fichier"
                  >
                    <Download className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  </button>

                  {currentUser?.id === file.userId && (
                    <>
                      <button 
                        className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                        onClick={() => startEditing(file)}
                        disabled={isEditing || editingFile !== null || editingDate !== null}
                        aria-label="Modifier le nom"
                      >
                        <Pencil className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-300" />
                      </button>
                      <button 
                        className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                        onClick={() => startEditingDate(file)}
                        disabled={isEditing || editingFile !== null || editingDate !== null}
                        aria-label="Modifier la date"
                      >
                        <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-300" />
                      </button>
                      <button 
                        className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                        onClick={() => setShowDeleteConfirm(file)}
                        aria-label="Supprimer le fichier"
                      >
                        <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedFile && (
        <MediaPlayer 
          file={selectedFile} 
          onClose={() => setSelectedFile(null)} 
        />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-md w-full mx-4">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Êtes-vous sûr de vouloir supprimer ce fichier ? Cette action est irréversible.
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-4 sm:mb-6 break-words">
              {showDeleteConfirm.name}
            </p>
            
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                disabled={isDeleting}
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                disabled={isDeleting}
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaList;